// ═══════════════════════════════════════════════════════════════════════════
// AI Command Schemas for block operations (Phase FIN.4)
// ═══════════════════════════════════════════════════════════════════════════
//
// Zod schemas describing the JSON the AI produces when it wants to create,
// insert, author, or modify blocks. Every schema is intentionally a plain
// data contract — it does NOT mutate anything. Translation to internal
// `EditorCommand`s happens in `blockDispatcher.ts`; the reducer pipeline
// (same path UI dispatches through) is the only place that ever writes to
// the document. This enforces the CLAUDE.md rule that AI never mutates the
// document directly.
//
// Design notes:
//   - Every action carries a literal `action: "..."` discriminator so the
//     top-level `blockAiCommandSchema` can be a Zod discriminated union.
//   - Payload shapes mirror the AutoCAD / editor terminology so a round-trip
//     through JSON is lossless for the parts we expose.
//   - Unknown entity payloads (for CREATE_BLOCK / CREATE_DYNAMIC_BLOCK) are
//     `z.unknown()` here; the dispatcher re-parses via drawing-model schemas
//     before handing them to the internal reducer. The contract package
//     does the same thing for `geometry/create-block-definition` — keeping
//     the ai-commands boundary consistent with the internal boundary.
//   - Coordinate objects use `{ x: number; y: number; z?: number }`. Z is
//     optional everywhere because AutoCAD 2D operations default Z to 0.
//   - Rotation values are expressed in DEGREES for AI-friendliness. The
//     dispatcher converts to radians where the target command expects it.

import { z } from "zod";

// ─── Shared building blocks ────────────────────────────────────────
export const aiPoint2DSchema = z.object({
  x: z.number(),
  y: z.number(),
});
export type AiPoint2D = z.infer<typeof aiPoint2DSchema>;

export const aiPoint3DSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number().default(0),
});
export type AiPoint3D = z.infer<typeof aiPoint3DSchema>;

// Attribute tag/value pair used by INSERT / UPDATE_ATTRIBUTE commands.
export const aiAttributeAssignmentSchema = z.object({
  tag: z.string().min(1),
  value: z.string(),
});
export type AiAttributeAssignment = z.infer<typeof aiAttributeAssignmentSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_BLOCK — author a new BLOCK definition (no insert yet).
// ═══════════════════════════════════════════════════════════════════════════
//
// Corresponds to AutoCAD BLOCK command (or WBLOCK with retain option). The
// AI supplies the name, base point, the child entity payloads, and optional
// metadata. `entities` are `z.unknown()` here — the dispatcher forwards them
// verbatim into `geometry/create-block-definition` which re-parses via the
// drawing-model schema. `postAction` mirrors the AutoCAD prompt answers:
// retain sources, convert sources to an INSERT, or delete them.
export const createBlockAiSchema = z.object({
  action: z.literal("CREATE_BLOCK"),
  name: z.string().min(1),
  basePoint: aiPoint2DSchema,
  entities: z.array(z.unknown()).default([]),
  explodable: z.boolean().default(true),
  scalable: z.enum(["uniform", "non-uniform"]).default("non-uniform"),
  description: z.string().default(""),
  // Ceiling 21 (US Survey Feet) — matches blockRecordSchema / $INSUNITS.
  insertionUnits: z.number().int().min(0).max(21).default(0),
  postAction: z.enum(["retain", "convert", "delete"]).default("retain"),
  // Optional: convert a selection of existing entities into the block.
  // When provided with `postAction: "convert"`, the reducer replaces those
  // entities with a single INSERT at `convertInsertPoint`.
  sourceEntityIds: z.array(z.string().min(1)).default([]),
  convertSpaceId: z.string().min(1).optional(),
  convertInsertPoint: aiPoint2DSchema.optional(),
  // Optional — let the AI flag the authored block as annotative so
  // `isAnnotative` flows into the BlockRecord. Annotation-scale lists
  // start empty and are populated via OBJECTSCALE commands.
  isAnnotative: z.boolean().default(false),
});
export type CreateBlockAiCommand = z.infer<typeof createBlockAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// INSERT_BLOCK — place a block definition as an INSERT entity.
// ═══════════════════════════════════════════════════════════════════════════
//
// Mirrors AutoCAD INSERT / MINSERT. Column / row fields are optional; when
// any of them are set we dispatch as an MINSERT (array). Rotation is in
// DEGREES; the dispatcher converts to radians for the internal command.
// `attributes` carry user values that overwrite matching ATTDEF defaults.
export const insertBlockAiSchema = z.object({
  action: z.literal("INSERT_BLOCK"),
  blockName: z.string().min(1),
  insertionPoint: aiPoint3DSchema,
  scaleX: z.number().default(1),
  scaleY: z.number().default(1),
  scaleZ: z.number().default(1),
  rotation: z.number().default(0),
  // Optional spaceId — when absent the dispatcher uses the active space.
  spaceId: z.string().min(1).optional(),
  // MINSERT support. `columnCount`/`rowCount` default to 1 (single
  // INSERT). Values >1 produce an MINSERT. The dispatcher emits one
  // `geometry/create-block-insert` command per copy to keep the undo
  // history coherent (AutoCAD records MINSERT as a single entity; our
  // current model expands it — matching INSERT*rows*cols behaviour).
  columnCount: z.number().int().min(1).default(1),
  rowCount: z.number().int().min(1).default(1),
  columnSpacing: z.number().default(0),
  rowSpacing: z.number().default(0),
  attributes: z.array(aiAttributeAssignmentSchema).default([]),
});
export type InsertBlockAiCommand = z.infer<typeof insertBlockAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// EXPLODE — turn one or more INSERT entities back into their child
// entities (one level of nesting only — matches AutoCAD EXPLODE default).
// ═══════════════════════════════════════════════════════════════════════════
export const explodeAiSchema = z.object({
  action: z.literal("EXPLODE"),
  insertIds: z.array(z.string().min(1)).min(1),
  resolveNested: z.enum(["none", "one-level"]).default("one-level"),
});
export type ExplodeAiCommand = z.infer<typeof explodeAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// UPDATE_ATTRIBUTE — change a single ATTRIB value on an INSERT.
// ═══════════════════════════════════════════════════════════════════════════
//
// The internal reducer matches by tag (DDATTE / ATTEDIT fallback) so the
// AI must specify the INSERT id + tag + new value. Optional `overrideHeight`
// / `overrideRotation` / `overridePosition` override the ATTDEF defaults
// per-INSERT — mirrors the Enhanced Attribute Editor Properties tab.
export const updateAttributeAiSchema = z.object({
  action: z.literal("UPDATE_ATTRIBUTE"),
  insertId: z.string().min(1),
  tag: z.string().min(1),
  newValue: z.string(),
  overrides: z
    .object({
      overrideHeight: z.number().positive().optional(),
      overrideRotation: z.number().optional(),
      overridePosition: aiPoint2DSchema.optional(),
      overrideStyle: z.string().min(1).optional(),
      invisible: z.boolean().optional(),
    })
    .optional(),
});
export type UpdateAttributeAiCommand = z.infer<typeof updateAttributeAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_DYNAMIC_BLOCK — author a block with parameters / actions /
// visibility states / lookup tables in a single AI turn.
// ═══════════════════════════════════════════════════════════════════════════
//
// This is the heaviest schema in the catalogue. The AI produces both the
// block definition and the full dynamic metadata; the dispatcher fans the
// payload out across multiple internal commands:
//   1. `geometry/create-block-definition` with the entities
//   2. `space/enter-block-edit` with the authored name
//   3. N × `geometry/add-dynamic-parameter` — one per parameter
//   4. N × `geometry/add-dynamic-action` — one per action
//   5. Optional `geometry/create-lookup-table` commands
//   6. `space/exit-block-edit action=save`
//
// Parameters / actions / tables are `z.unknown()` so the dispatcher re-parses
// them through the drawing-model schemas before forwarding. The AI is
// expected to produce shapes that drawing-model's
// `dynamicParameterSchema` / `dynamicActionSchema` / `lookupTableSchema`
// accept — the few-shot prompt documents the expected shapes.
export const createDynamicBlockAiSchema = z.object({
  action: z.literal("CREATE_DYNAMIC_BLOCK"),
  name: z.string().min(1),
  basePoint: aiPoint2DSchema,
  entities: z.array(z.unknown()).default([]),
  parameters: z.array(z.unknown()).default([]),
  actions: z.array(z.unknown()).default([]),
  lookupTables: z.array(z.unknown()).default([]),
  // Visibility states are authored inline under visibility parameters —
  // `parameters` entries of kind "visibility" already carry their own
  // `states` array, so this top-level field is advisory only. Kept for
  // few-shot ergonomics (the AI can group state lists by parameter name).
  visibilityStates: z
    .array(
      z.object({
        parameterName: z.string().min(1),
        states: z.array(
          z.object({
            name: z.string().min(1),
            invisibleEntityIds: z.array(z.string().min(1)).default([]),
          }),
        ),
      }),
    )
    .default([]),
  // Cosmetic / ergonomic metadata (mirrors CREATE_BLOCK)
  explodable: z.boolean().default(true),
  scalable: z.enum(["uniform", "non-uniform"]).default("non-uniform"),
  description: z.string().default(""),
  // Ceiling 21 (US Survey Feet) — matches blockRecordSchema / $INSUNITS.
  insertionUnits: z.number().int().min(0).max(21).default(0),
});
export type CreateDynamicBlockAiCommand = z.infer<
  typeof createDynamicBlockAiSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// SET_VISIBILITY_STATE — switch a dynamic-block INSERT's visibility.
// ═══════════════════════════════════════════════════════════════════════════
//
// `parameterName` is the visibility parameter's `name` field; the dispatcher
// resolves it to the parameterId by looking up the block definition. The
// internal reducer maps "state selection" to a per-INSERT override via
// `customProperties["<paramName>.currentState"]`.
export const setVisibilityStateAiSchema = z.object({
  action: z.literal("SET_VISIBILITY_STATE"),
  insertId: z.string().min(1),
  parameterName: z.string().min(1),
  stateName: z.string().min(1),
});
export type SetVisibilityStateAiCommand = z.infer<
  typeof setVisibilityStateAiSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// LOOKUP_SELECT — pick a lookup-table row by its output value.
// ═══════════════════════════════════════════════════════════════════════════
//
// AutoCAD's "reverse lookup" pattern — the user picks an output (part
// number, etc.) from the Properties palette and the cascade updates the
// driving input parameters. `outputValue` matches a lookup row; if
// multiple rows share the same output the first match wins (same as
// AutoCAD). The dispatcher resolves to the equivalent UPDATE_DYNAMIC_PARAMETER
// commands once the row is located.
export const lookupSelectAiSchema = z.object({
  action: z.literal("LOOKUP_SELECT"),
  insertId: z.string().min(1),
  parameterName: z.string().min(1),
  outputValue: z.union([z.string(), z.number()]),
});
export type LookupSelectAiCommand = z.infer<typeof lookupSelectAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// UPDATE_DYNAMIC_PARAMETER — change a single dynamic-block parameter value.
// ═══════════════════════════════════════════════════════════════════════════
//
// The value shape depends on the parameter `kind`. For simplicity (and
// AI-friendliness) we accept the three natural JSON primitives — the
// dispatcher validates against the actual parameter kind at runtime and
// rejects mismatches.
export const updateDynamicParameterAiSchema = z.object({
  action: z.literal("UPDATE_DYNAMIC_PARAMETER"),
  insertId: z.string().min(1),
  parameterName: z.string().min(1),
  value: z.union([z.number(), z.string(), z.boolean()]),
});
export type UpdateDynamicParameterAiCommand = z.infer<
  typeof updateDynamicParameterAiSchema
>;

// ═══════════════════════════════════════════════════════════════════════════
// SET_CANNOSCALE — switch the document's current annotation scale.
// ═══════════════════════════════════════════════════════════════════════════
export const setCannoscaleAiSchema = z.object({
  action: z.literal("SET_CANNOSCALE"),
  cannoscaleName: z.string().min(1),
});
export type SetCannoscaleAiCommand = z.infer<typeof setCannoscaleAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// CREATE_ATTDEF — add an attribute definition to a block (BEDIT).
// ═══════════════════════════════════════════════════════════════════════════
//
// Only valid inside an active BEDIT session; the dispatcher will emit a
// `space/enter-block-edit` pair around the add when not already editing.
export const createAttdefAiSchema = z.object({
  action: z.literal("CREATE_ATTDEF"),
  blockName: z.string().min(1),
  tag: z.string().min(1),
  prompt: z.string().default(""),
  defaultValue: z.string().default(""),
  insertionPoint: aiPoint2DSchema,
  height: z.number().positive(),
  rotation: z.number().default(0),
  widthFactor: z.number().positive().default(1),
  obliqueAngle: z.number().min(-85).max(85).default(0),
  styleName: z.string().min(1).default("Standard"),
  hJustification: z.number().int().min(0).max(5).default(0),
  vJustification: z.number().int().min(0).max(3).default(0),
  flags: z
    .object({
      invisible: z.boolean().default(false),
      constant: z.boolean().default(false),
      verify: z.boolean().default(false),
      preset: z.boolean().default(false),
      lockPosition: z.boolean().default(false),
      multiline: z.boolean().default(false),
    })
    .default(() => ({
      invisible: false,
      constant: false,
      verify: false,
      preset: false,
      lockPosition: false,
      multiline: false,
    })),
  // T7 (agent-bridge) local Codex round-16 P2 (github PR review) — OPTIONAL,
  // no "0" default, mirroring the CECOLOR fix directly below EXACTLY (same
  // bug shape: forcing a "0" default here made every AI-created ATTDEF carry
  // an explicit layer, which `stampCurrentLayerOntoCreateCommand` then SKIPS
  // (it only stamps when layer is undefined) — so AI ATTDEFs were forced onto
  // layer "0" regardless of the agent's SET_ACTIVE_LAYER, unlike the toolbar
  // path (`useAttributeDialogs.ts`, which already reads CLAYER explicitly).
  // Omitting the default lets the central chokepoint stamp the live CLAYER;
  // an AI request that DOES name a layer still sends it (explicit override
  // honoured).
  layer: z.string().optional(),
  // CECOLOR (EPIC-COLOR-UNION round-6 FINDING B) — OPTIONAL, no ByLayer default.
  // Forcing a ByLayer default here made every AI-created ATTDEF carry an explicit
  // color, which `stampCeColorOntoCreateCommand` then SKIPS (it only stamps when
  // color is undefined) — so AI ATTDEFs were forced ByLayer even in a non-ByLayer
  // CECOLOR drawing, unlike the toolbar path. Omitting the default lets the central
  // chokepoint stamp the live CECOLOR; an AI request that DOES name a color still
  // sends it (explicit override honoured).
  color: z.string().optional(),
  linetype: z.string().default("ByLayer"),
  lineweight: z.string().default("ByLayer"),
});
export type CreateAttdefAiCommand = z.infer<typeof createAttdefAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// UPDATE_ATTDEF — modify ATTDEF properties on an existing block.
// ═══════════════════════════════════════════════════════════════════════════
export const updateAttdefAiSchema = z.object({
  action: z.literal("UPDATE_ATTDEF"),
  blockName: z.string().min(1),
  attdefId: z.string().min(1),
  updates: z.object({
    prompt: z.string().optional(),
    defaultValue: z.string().optional(),
    height: z.number().positive().optional(),
    rotation: z.number().optional(),
    widthFactor: z.number().positive().optional(),
    obliqueAngle: z.number().min(-85).max(85).optional(),
    styleName: z.string().min(1).optional(),
    hJustification: z.number().int().min(0).max(5).optional(),
    vJustification: z.number().int().min(0).max(3).optional(),
    flags: z
      .object({
        invisible: z.boolean().optional(),
        constant: z.boolean().optional(),
        verify: z.boolean().optional(),
        preset: z.boolean().optional(),
        lockPosition: z.boolean().optional(),
        multiline: z.boolean().optional(),
      })
      .optional(),
  }),
});
export type UpdateAttdefAiCommand = z.infer<typeof updateAttdefAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// RENAME_ATTDEF_TAG — change an ATTDEF's tag; cascades to ATTRIBs.
// ═══════════════════════════════════════════════════════════════════════════
export const renameAttdefTagAiSchema = z.object({
  action: z.literal("RENAME_ATTDEF_TAG"),
  blockName: z.string().min(1),
  attdefId: z.string().min(1),
  newTag: z.string().min(1),
});
export type RenameAttdefTagAiCommand = z.infer<typeof renameAttdefTagAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// REMOVE_ATTDEF — drop an ATTDEF from a block.
// ═══════════════════════════════════════════════════════════════════════════
export const removeAttdefAiSchema = z.object({
  action: z.literal("REMOVE_ATTDEF"),
  blockName: z.string().min(1),
  attdefId: z.string().min(1),
  removeFromInserts: z.boolean().default(true),
});
export type RemoveAttdefAiCommand = z.infer<typeof removeAttdefAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// REORDER_ATTDEFS — change the prompt sequence.
// ═══════════════════════════════════════════════════════════════════════════
export const reorderAttdefsAiSchema = z.object({
  action: z.literal("REORDER_ATTDEFS"),
  blockName: z.string().min(1),
  attdefIds: z.array(z.string().min(1)).min(1),
});
export type ReorderAttdefsAiCommand = z.infer<typeof reorderAttdefsAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// PURGE_BLOCKS — delete unreferenced block definitions.
// ═══════════════════════════════════════════════════════════════════════════
export const purgeBlocksAiSchema = z.object({
  action: z.literal("PURGE_BLOCKS"),
  blockNames: z.array(z.string().min(1)).min(1),
});
export type PurgeBlocksAiCommand = z.infer<typeof purgeBlocksAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// RENAME_BLOCK — rename a block; cascades to all INSERTs.
// ═══════════════════════════════════════════════════════════════════════════
export const renameBlockAiSchema = z.object({
  action: z.literal("RENAME_BLOCK"),
  oldName: z.string().min(1),
  newName: z.string().min(1),
});
export type RenameBlockAiCommand = z.infer<typeof renameBlockAiSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Top-level AI command discriminated union
// ═══════════════════════════════════════════════════════════════════════════
//
// 16 block-related AI schemas:
//   Basic:       CREATE_BLOCK, INSERT_BLOCK, EXPLODE
//   Attribute:   UPDATE_ATTRIBUTE, CREATE_ATTDEF, UPDATE_ATTDEF,
//                RENAME_ATTDEF_TAG, REMOVE_ATTDEF, REORDER_ATTDEFS
//   Dynamic:     CREATE_DYNAMIC_BLOCK, SET_VISIBILITY_STATE,
//                LOOKUP_SELECT, UPDATE_DYNAMIC_PARAMETER
//   Annotative:  SET_CANNOSCALE
//   Lifecycle:   PURGE_BLOCKS, RENAME_BLOCK
export const blockAiCommandSchema = z.discriminatedUnion("action", [
  createBlockAiSchema,
  insertBlockAiSchema,
  explodeAiSchema,
  updateAttributeAiSchema,
  createDynamicBlockAiSchema,
  setVisibilityStateAiSchema,
  lookupSelectAiSchema,
  updateDynamicParameterAiSchema,
  setCannoscaleAiSchema,
  createAttdefAiSchema,
  updateAttdefAiSchema,
  renameAttdefTagAiSchema,
  removeAttdefAiSchema,
  reorderAttdefsAiSchema,
  purgeBlocksAiSchema,
  renameBlockAiSchema,
]);
export type BlockAiCommand = z.infer<typeof blockAiCommandSchema>;

// A batch of block commands the AI can emit in one response. The dispatcher
// resolves each in order. `BlockAiBatch` is what the few-shot examples show
// as the AI's expected `aiOutput`.
export const blockAiCommandBatchSchema = z.array(blockAiCommandSchema);
export type BlockAiCommandBatch = z.infer<typeof blockAiCommandBatchSchema>;

// Convenience: literal names for error messages / telemetry.
export const BLOCK_AI_ACTIONS = [
  "CREATE_BLOCK",
  "INSERT_BLOCK",
  "EXPLODE",
  "UPDATE_ATTRIBUTE",
  "CREATE_DYNAMIC_BLOCK",
  "SET_VISIBILITY_STATE",
  "LOOKUP_SELECT",
  "UPDATE_DYNAMIC_PARAMETER",
  "SET_CANNOSCALE",
  "CREATE_ATTDEF",
  "UPDATE_ATTDEF",
  "RENAME_ATTDEF_TAG",
  "REMOVE_ATTDEF",
  "REORDER_ATTDEFS",
  "PURGE_BLOCKS",
  "RENAME_BLOCK",
] as const;
export type BlockAiAction = (typeof BLOCK_AI_ACTIONS)[number];
